/*!
 * MONTVIELLE — cart drawer (mv-cart.js)
 * Every mutation goes through Shopify's cart AJAX API asking for the drawer section back
 * (Section Rendering API), so prices, discounts and recommendations are always rendered by
 * Liquid — JavaScript never formats money.
 *
 * One thing it does own: the moment an item lands. The drawer opens, the new line is marked
 * and scrolled to, because on a bag of five the new arrival otherwise appears below the fold
 * and the shopper is left wondering whether the click registered at all.
 *
 * API:    window.MV.cart.open() · close() · add(variantId, qty) · refresh()
 * Events: document 'mv:cart:changed' {item_count} · 'mv:track' {event, ...}
 */
(() => {
  'use strict';
  if (window.MV && window.MV.cart) return;

  const root = document.querySelector('[data-mv-cart]');
  if (!root) return;

  const html = document.documentElement;
  const SECTION = root.dataset.sectionId;
  const CART = root.dataset.cartUrl || '/cart';
  const dialog = document.getElementById('MvCart');
  const status = root.querySelector('[data-mv-cart-status]');
  let opener = null;

  const track = (event, detail) => document.dispatchEvent(new CustomEvent('mv:track', { detail: { event, ...detail } }));
  const say = (message) => { if (status) status.textContent = message; };

  /* ------------------------------------------------------------------ rendering */
  function applySection(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const fresh = doc.querySelector('[data-mv-cart]');
    const freshInner = doc.querySelector('[data-mv-cart-inner]');
    const liveInner = root.querySelector('[data-mv-cart-inner]');
    /* Never leave prices on screen the cart no longer agrees with: ask once more rather than
       silently keeping a stale bag. */
    if (!fresh || !freshInner || !liveInner) { refresh().catch(() => {}); return; }
    Object.keys(fresh.dataset).forEach((key) => { root.dataset[key] = fresh.dataset[key]; });
    liveInner.replaceChildren(...freshInner.childNodes);
  }

  async function refresh() {
    const response = await fetch(`${location.pathname}?sections=${SECTION}`, { headers: { Accept: 'application/json' } });
    const data = await response.json();
    if (data[SECTION]) applySection(data[SECTION]);
  }

  function afterChange() {
    const count = Number(root.dataset.count) || 0;
    document.dispatchEvent(new CustomEvent('mv:cart:changed', { detail: { item_count: count } }));
  }

  /* The line the shopper just created, found after Liquid has re-rendered the drawer. */
  function markFresh(key) {
    if (!key) return;
    const line = root.querySelector(`.mv-cl[data-key="${CSS.escape(String(key))}"]`);
    if (!line) return;
    line.classList.add('is-fresh');
    const tag = line.querySelector('.mv-cl__fresh');
    if (tag) tag.hidden = false;
    requestAnimationFrame(() => {
      const body = root.querySelector('.mv-cart__body');
      if (!body) return;
      const top = line.offsetTop - body.offsetTop - 8;
      body.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    });
  }

  /* /cart/add.js answers with the line itself for a form post and with {items:[…]} for a
     JSON post. Either way the key is what identifies the line in the re-rendered drawer. */
  const keyOf = (data) => (data && (data.key || (data.items && data.items[0] && data.items[0].key))) || null;

  async function post(path, body) {
    if (dialog) dialog.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: SECTION, sections_url: location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'That item could not be added.');
      if (data.sections && data.sections[SECTION]) applySection(data.sections[SECTION]); else await refresh();
      return data;
    } finally {
      if (dialog) dialog.removeAttribute('aria-busy');
    }
  }

  /* ------------------------------------------------------------------ open / close */
  function open(options = {}) {
    if (!dialog || dialog.open) return;
    opener = options.opener || document.activeElement;
    dialog.showModal();
    html.classList.add('mv-lock');
    if (window.MV.header) window.MV.header.lock('cart');
    requestAnimationFrame(() => dialog.querySelector('[data-mv-cart-close]')?.focus({ preventScroll: true }));
    track('view_cart', { item_count: Number(root.dataset.count) || 0 });
  }

  function close() {
    if (dialog && dialog.open) dialog.close();
  }

  if (dialog) {
    dialog.addEventListener('close', () => {
      html.classList.remove('mv-lock');
      if (window.MV.header) window.MV.header.unlock('cart');
      if (opener && opener.focus) opener.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
  }

  /* ------------------------------------------------------------------ interactions */
  root.addEventListener('click', async (e) => {
    const closer = e.target.closest('[data-mv-cart-close]');
    if (closer) { close(); return; }

    const sizes = e.target.closest('[data-mv-sizes]');
    if (sizes) {
      const tray = sizes.parentElement.querySelector('.mv-rc__sizes');
      const isOpen = sizes.getAttribute('aria-expanded') === 'true';
      sizes.setAttribute('aria-expanded', String(!isOpen));
      if (tray) tray.hidden = isOpen;
      return;
    }

    const adder = e.target.closest('[data-mv-add]');
    if (adder) {
      const id = Number(adder.dataset.mvAdd);
      adder.disabled = true;
      try {
        const data = await post('/cart/add.js', { items: [{ id, quantity: 1 }] });
        markFresh(keyOf(data));
        say('Added to your bag.');
        track('add_to_cart', { variant_id: id, source: adder.dataset.source || 'cart' });
        afterChange();
      } catch (error) {
        adder.disabled = false;
        say(error.message);
      }
      return;
    }

    const qty = e.target.closest('[data-mv-qty]');
    if (qty) {
      /* Minus at one does nothing. Deleting a line is what Remove is for, and a shopper
         trimming a quantity should never lose the item by one tap too many. */
      const now = Number(qty.dataset.qty) || 1;
      const next = qty.dataset.mvQty === 'up' ? now + 1 : now - 1;
      if (next < 1) return;
      await changeLine(qty.dataset.key, next, qty);
      return;
    }

    const undo = e.target.closest('[data-mv-undo]');
    if (undo) {
      const last = removed;
      if (!last) return;
      removed = null;
      hideFlash();
      try {
        const data = await post('/cart/add.js', { items: [{ id: last.id, quantity: last.quantity, properties: last.properties }] });
        markFresh(keyOf(data));
        say('Back in your bag.');
        afterChange();
      } catch (error) {
        say(error.message);
      }
      return;
    }

    const remove = e.target.closest('[data-mv-remove]');
    if (remove) {
      const line = remove.closest('.mv-cl');
      const title = line ? (line.querySelector('.mv-cl__title') || {}).textContent : '';
      removed = {
        id: Number(remove.dataset.variant),
        quantity: Number(remove.dataset.quantity) || 1,
        properties: remove.dataset.properties ? JSON.parse(remove.dataset.properties) : undefined,
      };
      await changeLine(remove.dataset.key, 0, remove);
      track('remove_from_cart', { line: remove.dataset.key });
      if (removed.id) showFlash(`${(title || 'That piece').trim()} removed.`);
    }
  });

  /* ------------------------------------------------------------------ the undo strip
     In flow above the footer, never over the checkout button, and outside the re-rendered
     inner so a cart refresh cannot wipe it mid-sentence. */
  const flash = root.querySelector('[data-mv-flash]');
  const flashText = root.querySelector('[data-mv-flash-text]');
  let removed = null;
  let flashTimer = 0;

  function hideFlash() {
    if (flash) flash.hidden = true;
  }

  function showFlash(message) {
    if (!flash || !flashText) return;
    flashText.textContent = message;
    flash.hidden = false;
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => { removed = null; hideFlash(); }, 9000);
  }

  async function changeLine(key, quantity, control) {
    const line = control ? control.closest('.mv-cl') : null;
    if (line) line.setAttribute('data-busy', '');
    try {
      await post('/cart/change.js', { id: key, quantity });
      afterChange();
      announceTotal();
    } catch (error) {
      say(error.message);
      if (line) line.removeAttribute('data-busy');
    }
  }

  /* A sighted shopper watches the subtotal move; a screen reader was told nothing at all.
     The figure is read back off the markup Liquid just rendered — never composed here. */
  function announceTotal() {
    const count = Number(root.dataset.count) || 0;
    const sum = root.querySelector('[data-mv-cart-total]');
    const items = count === 1 ? '1 item' : `${count} items`;
    say(sum ? `Bag updated. ${items}, subtotal ${sum.textContent.trim()}.` : `Bag updated. ${items}.`);
  }

  root.addEventListener('change', (e) => {
    const note = e.target.closest('[data-mv-note]');
    if (note) post('/cart/update.js', { note: note.value }).catch(() => {});
  });

  /* ------------------------------------------------------------------ add-to-cart anywhere on the site
     Captured before the theme's own handlers so every add opens this drawer, not Horizon's. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    submitForm(form);
  }, true);

  async function submitForm(form) {
    const button = form.querySelector('[type="submit"]');
    const data = new FormData(form);
    data.append('sections', SECTION);
    data.append('sections_url', location.pathname);
    if (button) button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || 'That item could not be added.');
      if (json.sections && json.sections[SECTION]) applySection(json.sections[SECTION]); else await refresh();
      track('add_to_cart', { variant_id: json.id, source: 'product_form' });
      open();
      markFresh(keyOf(json));
      say('Added to your bag.');
      afterChange();
    } catch (error) {
      say(error.message);
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('mv:cart:add', (e) => {
    const { id, quantity = 1 } = e.detail || {};
    if (id) add(id, quantity);
  });

  async function add(id, quantity = 1) {
    const data = await post('/cart/add.js', { items: [{ id: Number(id), quantity }] });
    open();
    markFresh(keyOf(data));
    say('Added to your bag.');
    afterChange();
    return data;
  }

  window.MV = window.MV || {};
  window.MV.cart = { open, close, add, refresh, get count() { return Number(root.dataset.count) || 0; } };

  // A cart edited in another tab, or restored from the back/forward cache, still shows the truth.
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh().then(afterChange).catch(() => {}); });
})();
